import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../../contexts/ThemeContext';
import { PiSunLight, PiMoonLight, PiDesktopLight } from 'react-icons/pi';
import clsx from 'clsx';

interface ThemeToggleProps {
  onToggle?: () => void;
}

type ThemeType = 'light' | 'dark' | 'system';

const ThemeToggle: React.FC<ThemeToggleProps> = ({ onToggle }) => {
  const { t } = useTranslation();
  const { theme, isThemeLoading, setThemeSelection } = useContext(ThemeContext);

  const handleThemeSelect = async (selectedTheme: ThemeType) => {
    await setThemeSelection(selectedTheme);
    if (onToggle) onToggle();
  };

  const themeOptions: Array<{ value: ThemeType; label: string; icon: React.ReactNode }> = [
    { value: 'light', label: t('Light'), icon: <PiSunLight size={15} /> },
    { value: 'dark', label: t('Dark'), icon: <PiMoonLight size={15} /> },
    { value: 'system', label: t('System'), icon: <PiDesktopLight size={15} /> },
  ];

  return (
    <div
      className={clsx(
        'flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-full transition-colors duration-300',
        isThemeLoading && 'opacity-50 cursor-not-allowed'
      )}
    >
      {themeOptions.map((option) => (
        <button
          key={option.value}
          onClick={() => handleThemeSelect(option.value)}
          disabled={isThemeLoading}
          className={clsx(
            'flex flex-1 items-center justify-center gap-1 px-2 py-1.5 rounded transition-all duration-200',
            theme === option.value
              ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          )}
          aria-label={`Select ${option.label} theme`}
          title={option.label}
        >
          {option.icon}
          <span className="text-xs font-medium hidden sm:inline">{option.label}</span>
        </button>
      ))}
    </div>
  );
};

export default ThemeToggle;
